import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { Loader } from '../../components/1elements';
import AuthorizationError from '../../components/2compounds/AuthorizationError';
import DashboardAssignmentManager from './DashboardAssignmentManager';
import DashboardDeletedTemplates from './DashboardDeletedTemplates';
import DashboardDraftPreview from './DashboardDraftPreview';
import DashboardLifecycleControls from './DashboardLifecycleControls';
import DashboardMetricDesigner from './DashboardMetricDesigner';
import DashboardVisualizationDesigner from './DashboardVisualizationDesigner';
import DashboardWidgetDesigner from './DashboardWidgetDesigner';
import {
  getDashboardErrorMessage,
  useCreateDashboardTemplateMutation,
  useGetDashboardTemplateQuery,
  useListDashboardTemplatesQuery,
  useUpdateDashboardDraftMutation
} from './dashboardApi';
import { DASHBOARD_STATUS } from './dashboardContract';
import {
  canCreateDashboard,
  canDesignDashboard,
  canPublishDashboard,
  canViewDashboard
} from './dashboardPermissions';
import {
  buildCreateDashboardTemplateRequest,
  getDashboardTemplateState,
  getDashboardTemplateStatusLabel,
  validateDashboardTemplateMetadata
} from './dashboardTemplateModel';

const EMPTY_FORM = { name: '', description: '' };

const versionLabel = (template) => {
  const version = template?.draftVersion ?? template?.publishedVersion;
  return version ? `Version ${version.versionNumber}` : null;
};

const DashboardTemplateCard = ({
  canEdit,
  isSelected,
  onEdit,
  onSelect,
  template
}) => (
  <Paper
    data-testid={`dashboard-template-${template.dashboardTemplateId}`}
    elevation={0}
    sx={{
      border: '1px solid',
      borderColor: isSelected ? 'primary.main' : 'divider',
      p: 2.5
    }}
  >
    <Stack
      alignItems="flex-start"
      direction="row"
      justifyContent="space-between"
      spacing={2}
    >
      <Box>
        <Typography component="h2" fontSize={18} fontWeight={700}>
          {template.name}
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="body2">
          {template.description || 'No description provided.'}
        </Typography>
      </Box>
      <Stack alignItems="flex-end" spacing={1}>
        <Chip
          color={
            template.ownership === 'customer' &&
            template.dashboardStatus === DASHBOARD_STATUS.PUBLISHED &&
            template.activeAssignmentCount > 0
              ? 'success'
              : 'default'
          }
          label={
            template.ownership === 'system'
              ? 'Fallback'
              : `${template.activeAssignmentCount ?? 0} active ${
                  template.activeAssignmentCount === 1
                    ? 'assignment'
                    : 'assignments'
                }`
          }
          size="small"
          variant="outlined"
        />
        <Chip label={getDashboardTemplateStatusLabel(template)} size="small" />
      </Stack>
    </Stack>

    <Divider sx={{ my: 2 }} />
    <Stack
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      spacing={1.5}
    >
      <Box>
        {versionLabel(template) && (
          <Typography color="text.secondary" variant="body2">
            {versionLabel(template)}
          </Typography>
        )}
        <Typography color="text.secondary" variant="body2">
          {template.ownership === 'system'
            ? 'Read-only fallback for locations without a matching published customer dashboard.'
            : template.dashboardStatus === DASHBOARD_STATUS.ARCHIVED
              ? 'Restore this dashboard before making further changes.'
              : 'Customer-owned dashboard configuration.'}
        </Typography>
      </Box>
      {template.ownership === 'customer' && (
        <Stack direction="row" spacing={1}>
          <Button
            disabled={isSelected}
            onClick={onSelect}
            size="small"
            variant={isSelected ? 'contained' : 'outlined'}
          >
            {isSelected ? 'Selected' : 'Manage'}
          </Button>
          {canEdit && (
            <Button onClick={onEdit} size="small" variant="outlined">
              Edit details
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  </Paper>
);

const DashboardMetadataFields = ({ errors, form, onChange }) => (
  <Stack spacing={2}>
    <TextField
      autoFocus
      error={Boolean(errors.name)}
      fullWidth
      helperText={errors.name || `${form.name.length}/150 characters`}
      inputProps={{ maxLength: 151 }}
      label="Dashboard name"
      onChange={(event) => onChange('name', event.target.value)}
      required
      value={form.name}
    />
    <TextField
      error={Boolean(errors.description)}
      fullWidth
      helperText={
        errors.description || `${form.description.length}/1000 characters`
      }
      inputProps={{ maxLength: 1001 }}
      label="Description"
      minRows={3}
      multiline
      onChange={(event) => onChange('description', event.target.value)}
      value={form.description}
    />
  </Stack>
);

const DashboardManagement = () => {
  const canView = canViewDashboard();
  const canCreate = canCreateDashboard();
  const canDesign = canDesignDashboard();
  const canPublish = canPublishDashboard();
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [createErrors, setCreateErrors] = useState({});
  const [cloneSystemDefault, setCloneSystemDefault] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editErrors, setEditErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const templatesQuery = useListDashboardTemplatesQuery(undefined, {
    skip: !canView
  });
  const templateState = getDashboardTemplateState(
    templatesQuery.data?.templates
  );
  const selectedCustomerSummary =
    templateState.customerTemplates.find(
      (template) => template.dashboardTemplateId === selectedTemplateId
    ) ??
    templateState.customerTemplates[0] ??
    null;
  const customerTemplateId = selectedCustomerSummary?.dashboardTemplateId;
  const detailQuery = useGetDashboardTemplateQuery(customerTemplateId, {
    skip: !canView || !customerTemplateId
  });
  const [createTemplate, createState] = useCreateDashboardTemplateMutation();
  const [updateDraft, updateState] = useUpdateDashboardDraftMutation();
  const [startDraft, startDraftState] = useUpdateDashboardDraftMutation();
  const customerTemplate =
    (customerTemplateId ? detailQuery.data : null) ??
    selectedCustomerSummary ??
    null;

  const setFormValue = (setter, errorSetter, field, value) => {
    setter((current) => ({ ...current, [field]: value }));
    errorSetter((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const openCreate = () => {
    setCreateForm(EMPTY_FORM);
    setCreateErrors({});
    setCloneSystemDefault(true);
    setSuccessMessage(null);
    createState.reset?.();
    setCreateOpen(true);
  };

  const submitCreate = async (event) => {
    event.preventDefault();
    const validation = validateDashboardTemplateMetadata(createForm);
    setCreateErrors(validation.errors);
    if (!validation.isValid) return;

    try {
      const createdTemplate = await createTemplate(
        buildCreateDashboardTemplateRequest(
          validation.values,
          cloneSystemDefault
        )
      ).unwrap();
      setSelectedTemplateId(createdTemplate.dashboardTemplateId);
      setCreateOpen(false);
      setSuccessMessage('Customer dashboard draft created.');
    } catch (_error) {
      // RTK Query exposes the normalized error through createState below.
    }
  };

  const openEdit = (template = customerTemplate) => {
    if (!template) return;
    setSelectedTemplateId(template.dashboardTemplateId);
    setEditForm({
      name: template.name ?? '',
      description: template.description ?? ''
    });
    setEditErrors({});
    setSuccessMessage(null);
    updateState.reset?.();
    setEditOpen(true);
  };

  const submitEdit = async (event) => {
    event.preventDefault();
    const validation = validateDashboardTemplateMetadata(editForm);
    setEditErrors(validation.errors);
    if (!validation.isValid) return;

    try {
      await updateDraft({
        dashboardTemplateId: customerTemplate.dashboardTemplateId,
        ...validation.values
      }).unwrap();
      setEditOpen(false);
      setSuccessMessage('Dashboard draft details updated.');
    } catch (_error) {
      // RTK Query exposes the normalized error through updateState below.
    }
  };

  const startMetricDraft = async () => {
    if (!customerTemplate) return;
    startDraftState.reset?.();
    setSuccessMessage(null);
    try {
      await startDraft({
        dashboardTemplateId: customerTemplate.dashboardTemplateId,
        name: customerTemplate.name,
        description: customerTemplate.description ?? null
      }).unwrap();
      setSuccessMessage(
        'New dashboard draft created from the published dashboard.'
      );
    } catch (_error) {
      // RTK Query exposes the normalized error through startDraftState below.
    }
  };

  if (!canView) return <AuthorizationError />;
  if (templatesQuery.isLoading) return <Loader />;
  if (templatesQuery.error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {getDashboardErrorMessage(templatesQuery.error)}
      </Alert>
    );
  }
  if (!templateState.systemTemplate) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        The system default dashboard is unavailable.
      </Alert>
    );
  }

  const canEditCustomer =
    Boolean(canDesign && customerTemplate) &&
    customerTemplate.dashboardStatus !== DASHBOARD_STATUS.ARCHIVED;

  return (
    <Box
      data-testid="dashboard-management"
      sx={{ height: '100%', overflowY: 'auto', pb: 4, pr: 2 }}
    >
      <Stack
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Typography component="h1" fontSize={26} fontWeight={700}>
            Dashboard configuration
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Create reusable dashboards, publish their versions, then assign them
            to entities or industry types.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          {canPublish && (
            <DashboardDeletedTemplates onSuccess={setSuccessMessage} />
          )}
          {canCreate && (
            <Button onClick={openCreate} variant="contained">
              Create customer dashboard
            </Button>
          )}
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ mt: 3 }}>
        <AlertTitle>How a location gets its dashboard</AlertTitle>A published
        entity assignment takes priority, followed by a published industry-type
        assignment. A location with neither uses the single system default
        dashboard.
      </Alert>

      {successMessage && (
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          sx={{ mt: 2 }}
        >
          {successMessage}
        </Alert>
      )}
      {(detailQuery.isFetching || templatesQuery.isFetching) && (
        <LinearProgress sx={{ mt: 2 }} />
      )}
      {detailQuery.error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {getDashboardErrorMessage(detailQuery.error)}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
          mt: 3
        }}
      >
        <DashboardTemplateCard
          canEdit={false}
          isSelected={false}
          template={templateState.systemTemplate}
        />

        {templateState.customerTemplates.length ? (
          templateState.customerTemplates.map((templateSummary) => {
            const isSelected =
              templateSummary.dashboardTemplateId === customerTemplateId;
            const template = isSelected
              ? (customerTemplate ?? templateSummary)
              : templateSummary;
            return (
              <DashboardTemplateCard
                canEdit={
                  Boolean(canDesign && isSelected) &&
                  template.dashboardStatus !== DASHBOARD_STATUS.ARCHIVED
                }
                isSelected={isSelected}
                key={templateSummary.dashboardTemplateId}
                onEdit={() => openEdit(template)}
                onSelect={() => {
                  setSelectedTemplateId(templateSummary.dashboardTemplateId);
                  setSuccessMessage(null);
                }}
                template={template}
              />
            );
          })
        ) : (
          <Paper
            elevation={0}
            sx={{
              border: '1px dashed',
              borderColor: 'divider',
              display: 'grid',
              minHeight: 190,
              p: 3,
              placeItems: 'center',
              textAlign: 'center'
            }}
          >
            <Box>
              <Typography fontWeight={700}>No customer dashboard</Typography>
              <Typography
                color="text.secondary"
                sx={{ mt: 0.5 }}
                variant="body2"
              >
                Create a draft to customize metrics, widgets, and graphs. The
                system default remains live while the draft is prepared.
              </Typography>
              {canCreate && (
                <Button onClick={openCreate} sx={{ mt: 2 }} variant="outlined">
                  Create dashboard draft
                </Button>
              )}
            </Box>
          </Paper>
        )}
      </Box>

      {customerTemplate && (
        <>
          <DashboardLifecycleControls
            canManage={canPublish}
            customerTemplate={customerTemplate}
            hasAssignments={customerTemplate.activeAssignmentCount > 0}
            onDeleted={() => setSelectedTemplateId(null)}
            onSuccess={setSuccessMessage}
          />
          <DashboardAssignmentManager
            activeAssignmentCount={customerTemplate.activeAssignmentCount}
            canManage={canPublish}
            dashboardStatus={customerTemplate.dashboardStatus}
            dashboardTemplateId={customerTemplate.dashboardTemplateId}
            onSuccess={setSuccessMessage}
          />
        </>
      )}

      {customerTemplate &&
        customerTemplate.dashboardStatus !== DASHBOARD_STATUS.ARCHIVED &&
        !detailQuery.isFetching &&
        detailQuery.data &&
        (detailQuery.data.draftVersion ? (
          <>
            <DashboardMetricDesigner
              canEdit={canEditCustomer}
              dashboardTemplateId={customerTemplate.dashboardTemplateId}
              draftVersion={detailQuery.data.draftVersion}
            />
            <DashboardWidgetDesigner
              canEdit={canEditCustomer}
              dashboardTemplateId={customerTemplate.dashboardTemplateId}
              draftVersion={detailQuery.data.draftVersion}
            />
            <DashboardVisualizationDesigner
              canEdit={canEditCustomer}
              dashboardTemplateId={customerTemplate.dashboardTemplateId}
              draftVersion={detailQuery.data.draftVersion}
            />
            <DashboardDraftPreview
              draftVersion={detailQuery.data.draftVersion}
            />
          </>
        ) : (
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              mt: 4,
              p: 3
            }}
          >
            <Typography component="h2" fontSize={22} fontWeight={700}>
              Dashboard design
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.75 }}>
              The published dashboard has no editable draft. Start a new draft
              to change its metrics, widgets, and graphs while the published
              version remains live.
            </Typography>
            {startDraftState.error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {getDashboardErrorMessage(startDraftState.error)}
              </Alert>
            )}
            {canEditCustomer && (
              <Button
                disabled={startDraftState.isLoading}
                onClick={startMetricDraft}
                sx={{ mt: 2 }}
                variant="contained"
              >
                {startDraftState.isLoading
                  ? 'Starting draft…'
                  : 'Start dashboard changes'}
              </Button>
            )}
          </Paper>
        ))}

      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={() => !createState.isLoading && setCreateOpen(false)}
        open={createOpen}
      >
        <Box component="form" noValidate onSubmit={submitCreate}>
          <DialogTitle>Create customer dashboard</DialogTitle>
          <DialogContent>
            <Stack spacing={2.5} sx={{ pt: 1 }}>
              <DashboardMetadataFields
                errors={createErrors}
                form={createForm}
                onChange={(field, value) =>
                  setFormValue(setCreateForm, setCreateErrors, field, value)
                }
              />
              <FormControl>
                <FormLabel id="dashboard-starting-point-label">
                  Starting point
                </FormLabel>
                <RadioGroup
                  aria-labelledby="dashboard-starting-point-label"
                  onChange={(event) =>
                    setCloneSystemDefault(event.target.value === 'clone')
                  }
                  value={cloneSystemDefault ? 'clone' : 'blank'}
                >
                  <FormControlLabel
                    control={<Radio />}
                    label="Clone the system default (recommended)"
                    value="clone"
                  />
                  <FormControlLabel
                    control={<Radio />}
                    label="Start with a blank dashboard"
                    value="blank"
                  />
                </RadioGroup>
              </FormControl>
              {createState.error && (
                <Alert severity="error">
                  {getDashboardErrorMessage(createState.error)}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              disabled={createState.isLoading}
              onClick={() => setCreateOpen(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={createState.isLoading}
              type="submit"
              variant="contained"
            >
              {createState.isLoading ? 'Creating…' : 'Create dashboard'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={() => !updateState.isLoading && setEditOpen(false)}
        open={editOpen}
      >
        <Box component="form" noValidate onSubmit={submitEdit}>
          <DialogTitle>Edit dashboard details</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <DashboardMetadataFields
                errors={editErrors}
                form={editForm}
                onChange={(field, value) =>
                  setFormValue(setEditForm, setEditErrors, field, value)
                }
              />
              {updateState.error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {getDashboardErrorMessage(updateState.error)}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              disabled={updateState.isLoading}
              onClick={() => setEditOpen(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={updateState.isLoading}
              type="submit"
              variant="contained"
            >
              {updateState.isLoading ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default DashboardManagement;
